import React, { useState, useEffect } from 'react';
import { notificationService } from '../services/notificationService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faTimes, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const InAppNotifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        // Register callback for in-app notifications
        const unsubscribe = notificationService.addInAppNotificationCallback((notification) => {
            const newNotification = {
                ...notification,
                id: Date.now() + Math.random(), // Simple unique ID
                timestamp: new Date().toISOString()
            };

            setNotifications(prev => [newNotification, ...prev.slice(0, 4)]); // Keep only 5 most recent

            // Auto-remove after 10 seconds
            setTimeout(() => {
                removeNotification(newNotification.id);
            }, 60000);
        });

        return unsubscribe;
    }, []);

    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    const clearAllNotifications = () => {
        setNotifications([]);
    };

    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (!isVisible || notifications.length === 0) {
        return null;
    }

    return (
        <div className="position-fixed" style={{
            top: '20px',
            right: '20px',
            zIndex: 9999,
            maxWidth: '400px',
            padding: '10px',
            backgroundColor: '#bbb',
        }}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3 px-2">
                <div className="d-flex align-items-center">
                    <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-2"
                         style={{ width: '24px', height: '24px' }}>
                        <FontAwesomeIcon icon={faBell} className="text-white" size="xs" />
                    </div>
                    <h6 className="m-0 text-dark fw-semibold" style={{ fontSize: '0.9rem' }}>
                        Notifications {notifications.length > 0 && (
                        <span className="badge bg-primary rounded-pill ms-1" style={{ fontSize: '0.7rem' }}>
                                {notifications.length}
                            </span>
                    )}
                    </h6>
                </div>
                <div className="d-flex align-items-center gap-1">
                    {notifications.length > 1 && (
                        <button
                            className="btn btn-sm btn-outline-secondary border-0 text-muted"
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={clearAllNotifications}
                            title="Clear all notifications"
                        >
                            Clear All
                        </button>
                    )}
                    <button
                        className="btn btn-sm btn-outline-secondary border-0 text-muted"
                        style={{ padding: '0.25rem 0.5rem' }}
                        onClick={() => setIsVisible(false)}
                        title="Hide notifications"
                    >
                        <FontAwesomeIcon icon={faTimes} size="sm" />
                    </button>
                </div>
            </div>

            <div className="d-flex flex-column gap-2">
                {notifications.map((notification) => (
                    <div
                        key={notification.id}
                        className="card shadow-sm border-0 mb-2"
                        style={{
                            animation: 'slideInRight 0.3s ease-out',
                            backgroundColor: '#ffffff',
                            borderLeft: '4px solid #0d6efd',
                            maxWidth: '380px'
                        }}
                    >
                        <div className="card-body p-3 position-relative">
                            {/* Close button */}
                            <button
                                type="button"
                                className="btn-close position-absolute"
                                style={{ top: '8px', right: '8px', fontSize: '0.75rem' }}
                                onClick={() => removeNotification(notification.id)}
                                aria-label="Close"
                            />

                            <div className="d-flex align-items-start pe-3">
                                {/* Notification icon */}
                                <div className="flex-shrink-0 me-3">
                                    {notification.icon  ? (
                                        <img
                                            src={notification.icon}
                                            alt=""
                                            width="32"
                                            height="32"
                                            className="rounded"
                                            style={{ objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <div className="bg-primary rounded d-flex align-items-center justify-content-center"
                                             style={{ width: '32px', height: '32px' }}>
                                            <FontAwesomeIcon icon={faBell} className="text-white" size="sm" />
                                        </div>
                                    )}
                                </div>

                                <div className="flex-grow-1">
                                    {/* Title */}
                                    <h6 className="mb-1 fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>
                                        {notification.title}
                                    </h6>

                                    {/* Body */}
                                    {notification.body && (
                                        <p className="mb-2 text-muted" style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                                            {notification.body}
                                        </p>
                                    )}

                                    {/* Custom data */}
                                    {notification.data?.customData && (
                                        <div className="mb-2 p-2 rounded" style={{ backgroundColor: '#f8f9fa', fontSize: '0.8rem' }}>
                                            <FontAwesomeIcon icon={faInfoCircle} className="me-1 text-primary" />
                                            <span className="text-muted">{notification.data.customData}</span>
                                        </div>
                                    )}

                                    {/* Footer */}
                                    <div className="d-flex justify-content-between align-items-center mt-2">
                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            {formatTime(notification.timestamp)}
                                        </small>

                                        {notification.data?.url && (
                                            <a
                                                href={notification.data.url}
                                                className="btn btn-sm btn-primary px-3"
                                                style={{ fontSize: '0.75rem' }}
                                                onClick={() => removeNotification(notification.id)}
                                            >
                                                View
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <style>
                {`
                @keyframes slideInRight {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                `}
            </style>
        </div>
    );
};

// Test component for debugging
const NotificationTester = () => {
    const [testCount, setTestCount] = useState(0);

    const sendTestNotification = () => {
        notificationService.testNotification();
        setTestCount(prev => prev + 1);
    };

    const sendCustomTestNotification = () => {
        const customNotification = {
            title: `Custom Test ${testCount + 1}`,
            body: 'This is a custom test notification with more details and data',
            icon: '/icon-192x192.png',
            data: {
                customData: 'This is custom data in the notification',
                url: '#test'
            },
            timestamp: new Date().toISOString()
        };

        notificationService.triggerInAppNotification(customNotification);
        setTestCount(prev => prev + 1);
    };

    return (
        <div className="mb-3 p-3 border rounded bg-light">
            <h6>Notification Testing</h6>
            <p className="small text-muted">Use these buttons to test the notification system</p>

            <div className="d-flex gap-2">
                <button
                    className="btn btn-sm btn-primary"
                    onClick={sendTestNotification}
                >
                    Test Basic Notification
                </button>

                <button
                    className="btn btn-sm btn-secondary"
                    onClick={sendCustomTestNotification}
                >
                    Test Custom Notification
                </button>
            </div>

            {testCount > 0 && (
                <small className="text-muted d-block mt-2">
                    Sent {testCount} test notification{testCount !== 1 ? 's' : ''}
                </small>
            )}
        </div>
    );
};

// Main component that includes both the notification display and tester
const InAppNotificationSystem = ({ showTester = false }) => {
    return (
        <>
            {showTester && <NotificationTester />}
            <InAppNotifications />
        </>
    );
};

export default InAppNotificationSystem;